<template>
    <div class="page reset-pass-page">
        <div class="page-wrap">
            <img
                src="@/assets/authorization/password.png"
                class="img-password"
            />

            <div class="form-wrap">
                <div class="form-content">
                    <header-main>
                        {{ $t("components.authorization.recovery.header") }}
                    </header-main>

                    <form novalidate>
                        <div
                            :class="[
                                'input-wrap',
                                {
                                    input_error:
                                        v$.form.password.$errors.length,
                                },
                            ]"
                        >
                            <input-field
                                v-model="v$.form.password.$model"
                                class="input-password"
                                :type="showPassword ? 'text' : 'password'"
                                :placeholder="
                                    $t('components.authorization.password')
                                "
                            />

                            <button
                                class="input-password-icon"
                                @click.prevent="showPassword = !showPassword"
                            >
                                <i
                                    :class="
                                        showPassword
                                            ? 'fas fa-eye-slash'
                                            : 'fas fa-eye'
                                    "
                                ></i>
                            </button>
                        </div>

                        <div class="input-errors">
                            <span
                                v-for="(error, index) of v$.form.password
                                    .$errors"
                                :key="index"
                            >
                                {{
                                    $t(
                                        `components.authorization.${error.$message}`
                                    )
                                }}
                            </span>
                        </div>

                        <div
                            :class="[
                                'input-wrap',
                                {
                                    input_error:
                                        v$.form.passwordConfirm.$errors.length,
                                },
                            ]"
                        >
                            <input-field
                                v-model="v$.form.passwordConfirm.$model"
                                class="input-password"
                                :type="
                                    showPasswordConfirm ? 'text' : 'password'
                                "
                                :placeholder="
                                    $t(
                                        'components.authorization.recovery.password_confirm'
                                    )
                                "
                            />

                            <button
                                class="input-password-icon"
                                @click.prevent="
                                    showPasswordConfirm = !showPasswordConfirm
                                "
                            >
                                <i
                                    :class="
                                        showPasswordConfirm
                                            ? 'fas fa-eye-slash'
                                            : 'fas fa-eye'
                                    "
                                ></i>
                            </button>
                        </div>

                        <div class="input-errors">
                            <span
                                v-for="(error, index) of v$.form.passwordConfirm
                                    .$errors"
                                :key="index"
                            >
                                {{
                                    $t(
                                        `components.authorization.recovery.${error.$message}`
                                    )
                                }}
                            </span>
                        </div>
                    </form>

                    <button-loading
                        class="btn"
                        :loading="button.loading"
                        :disabled="button.disabled"
                        @click="resetPassword"
                    >
                        {{ $t("components.authorization.recovery.button") }}
                    </button-loading>

                    <router-link
                        to="/profile/forgot-password"
                        class="bottom-link"
                    >
                        {{ $t("components.authorization.signin.link") }}
                    </router-link>

                    <LanguageSelect />

                    <auth-landing-link/>
                </div>

                <img src="@/assets/authorization/wave.svg" class="wave" />

                <img src="@/assets/authorization/ronm-bg.png" class="ronm-bg" />

                <img
                    src="@/assets/authorization/cilinder-bf.png"
                    class="cilinder-bf"
                />
            </div>
        </div>
    </div>
</template>

<script>
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";
import InputField from "@/components/Recurring/InputField";
import HeaderMain from "@/components/Recurring/HeaderMain.vue";
import AuthLandingLink from "@/components/Auth/AuthLandingLink.vue";
import LanguageSelect from "@/components/Recurring/LanguageSelect.vue";
import ToastContent from "@/components/Recurring/ToastContent.vue";

import { mapActions } from "vuex";
import { withProps, clearToasts } from "mosha-vue-toastify";

import useVuelidate from "@vuelidate/core";
import {
    required,
    email,
    minLength,
    sameAs,
    helpers,
} from "@vuelidate/validators";

export default {
    name: "ResetPasswordPage",

    components: {
        ButtonLoading,
        InputField,
        HeaderMain,
        AuthLandingLink,
        LanguageSelect,
        ToastContent,
    },

    setup() {
        return { v$: useVuelidate(), clearToasts };
    },

    data() {
        return {
            showPassword: false,
            showPasswordConfirm: false,

            form: {
                password: "",
                passwordConfirm: "",
            },

            button: {
                loading: false,
                disabled: false,
            },
        };
    },

    validations() {
        return {
            form: {
                password: {
                    required: helpers.withMessage("required", required),
                    min: helpers.withMessage(
                        "password_min_length_error",
                        minLength(6)
                    ),
                },
                passwordConfirm: {
                    sameAs: helpers.withMessage(
                        "password_not_match_error",
                        sameAs(this.form.password)
                    ),
                },
            },
        };
    },

    methods: {
        ...mapActions("auth", ["RESET_PASSWORD"]),

        setBtnLoading() {
            if (this.button.loading) {
                this.button.loading = false;
                this.button.disabled = false;

                return;
            }

            this.button.loading = true;
            this.button.disabled = true;
        },

        async resetPassword() {
            const isFormCorrect = await this.v$.$validate();

            if (!isFormCorrect) return;

            const payload = {
                password: this.form.password,
                email: this.$route.query.email,
                token: this.$route.query.token,
            };

            this.setBtnLoading();

            await this.RESET_PASSWORD(payload)
                .then(() => {
                    this.$moshaToast(
                        withProps(ToastContent, {
                            msg: this.$t(
                                "components.authorization.recovery.toast_success"
                            ),
                            type: "success",
                        }),
                        {
                            hideProgressBar: true,
                        }
                    );

                    this.$router.push({ name: "SignIn" });
                })
                .catch((error) => {
                    let toastMsg = this.$t(
                        "components.authorization.recovery.toast_error"
                    );

                    let responseMsg = error?.response?.data?.description;

                    if (
                        responseMsg === "This password reset token is invalid."
                    ) {
                        toastMsg = this.$t(
                            "components.authorization.recovery.token_toast_error"
                        );
                    }

                    this.$moshaToast(
                        withProps(ToastContent, {
                            msg: toastMsg,
                            type: "error",
                        }),
                        {
                            hideProgressBar: true,
                            timeout: 10000,
                        }
                    );
                })
                .finally(() => this.setBtnLoading());
        },
    },

    unmounted() {
        this.clearToasts();
    },
};
</script>

<style lang="scss" scoped>
@use "@/styles/pages/auth/auth.scss";
</style>
